import ChainedError from "chained-error";
import { isAfter, isBefore, parse as parseDateStr } from "date-fns";
import { isString } from "lodash";
import * as z from "zod/v4";
import { ensureNN, stripCurrencyPrefix, toNum } from "./coercions";
import { gracefullyParseDate, parseDate, primaryDateFormat } from "./datetime";
export { z };
// In transformation types last type in the name must be output type
// Eg. The output type of strBool is boolean
//     where as output type of boolStr would be string
export const strBool = (defaultVal = false) => maybeStr.transform(it => {
    if (it != null)
        return it === "true";
    return defaultVal;
});
export const strNum = (defaultVal) => maybeStr.transform(it => {
    if (it != null)
        return toNum(it);
    return defaultVal;
});
// String containing a number - intended to preserve precision
export const numStr = z
    .string()
    .or(z.number())
    .transform((it) => {
    if (typeof it !== "string")
        return `${it}`;
    return it.trim();
})
    .refine(it => `${it}`.match(/^(\+|-)?\s*\d+(\.\d+)?$/));
export const posIntStr = z
    .string()
    .or(z.number())
    .transform((it) => {
    if (typeof it !== "string")
        return `${it}`;
    return it.trim();
})
    .refine(it => `${it}`.match(/^\d+$/));
export const maybeNumStr = numStr.nullish();
export const trimmedStr = z.string().transform(it => it.trim());
export const trimmedUfStr = trimmedStr
    .transform(it => (!it ? undefined : it))
    .nullish();
export const trimmedNumStr = trimmedStr
    .transform(_ => _.replace(/,/g, ""))
    .refine(it => it.match(/^\d+(\.\d+)?$/), {
    message: "invalid number",
});
export const trimmedUfNumStr = trimmedUfStr
    .transform(_ => _?.replace(/,/g, ""))
    .refine(it => !it || it.match(/^\d+(\.\d+)?$/), {
    message: "invalid number",
});
export const trimmedUfStrNum = trimmedUfStr
    .transform(_ => (_ ? Number.parseFloat(_.replace(/,/g, "")) : undefined))
    .refine(_ => !_ || !Number.isNaN(_), { message: "invalid number" });
export const trimmedPinCodeStr = trimmedStr.refine(it => !it || it.match(/^(\d{6}|\d{8})$/), { message: "pincode is expected to have 6/8 digits" });
export const trimmedUfStrWithoutMultiple = trimmedUfStr.refine(it => !it || !it.includes(","), "Should be a single name");
export const trimmedNEStr = trimmedStr.refine(it => !!it, "expected to not be empty");
const englishNamePattern = /^[A-Za-z][A-Za-z\s.'-]*$/;
export const englishNameStr = trimmedNEStr.refine(value => englishNamePattern.test(value), {
    message: "only English letters, spaces, apostrophes, hyphens, or periods allowed",
});
export const maybeUfPosIntStr = (fallback, replacements) => z
    .string()
    .or(z.number())
    .transform((it) => {
    if (!it)
        return undefined;
    let str = `${it}`.trim();
    if (replacements)
        str = str.replace(replacements, "");
    if (fallback?.test(str))
        return undefined;
    return str || undefined;
})
    .refine(it => (it ? it.match(/^\d+$/) : true));
export const nonEmptyStr = trimmedStr.refine(it => it.length > 0, {
    message: "must not be empty",
});
export const obj = z.object;
export const zEnum = z.enum;
export const zSortBy = zEnum(["asc", "desc"]);
export const fn = z.function;
export const num = z.number();
export const maybeNum = num.nullish();
export const msse = z
    .date()
    .or(z.number())
    .transform(it => +it);
export const maybeMsse = msse.nullish();
export const msseDate = z.number().transform(it => new Date(it));
export const strDate = z
    .number()
    .or(z.string())
    .or(z.date())
    .transform(it => {
    if (typeof it === "number")
        return new Date(it);
    if (typeof it === "string")
        return parseDate(it);
    return it;
});
export const str = z.string();
export const maybeStr = str.nullish();
export const bool = z.boolean();
export const maybeBool = bool.nullish();
export const NStrRec = z.record(str, str.nullish());
export const vPromise = z.promise(z.void());
export const strOrNum = z.string().or(z.number());
export const maybeArr = (t) => t.or(z.array(t));
export const dateStr = str.superRefine((it, ctx) => {
    try {
        parseDate(it);
    }
    catch (e) {
        ctx.addIssue({
            code: "custom",
            message: `not in expected format: ${primaryDateFormat}`,
        });
    }
});
export const constrainedDateStr = (constraints) => str.superRefine((it, ctx) => {
    try {
        const parsed = parseDateStr(it, primaryDateFormat, new Date());
        if (constraints.min && isBefore(parsed, constraints.min)) {
            ctx.addIssue({
                code: "custom",
                message: "too far back",
            });
        }
        if (constraints.max && isAfter(parsed, constraints.max)) {
            ctx.addIssue({
                code: "custom",
                message: "too soon",
            });
        }
    }
    catch (e) {
        ctx.addIssue({
            code: "custom",
            message: `not in expected format: ${primaryDateFormat}`,
        });
    }
});
export const dateRange = obj({
    start: dateStr.nullish(),
    end: dateStr.nullish(),
});
export const ignoreUf = z.any().transform(() => undefined);
export const phoneStr = str.transform((ph, ctx) => {
    const trimmed = ph.replace(/(\s|-|,|_)*/g, "");
    const match = trimmed.match(/^\+?(91)?(\d{10})$/);
    if (match)
        return `+91${match[2]}`;
    ctx.addIssue({
        code: "custom",
        message: "not a 10 digit number",
    });
    return trimmed;
});
export const ref = (wrapped) => obj({
    value: wrapped.nullish(),
});
export const collectionRef = (wrapped) => obj({
    values: wrapped.array(),
});
export const maybeRef = (wrapped) => ref(wrapped).nullish();
export const maybeCollectionRef = (wrapped) => collectionRef(wrapped).nullish();
/**
 * Given an object schema make certain properties nullish
 */
export const optProps = (schema, propertyNames) => {
    const optionalProperties = propertyNames.reduce((obj, propertyName) => ({
        ...obj,
        [propertyName]: schema.shape[propertyName].nullish(),
    }), schema.shape);
    // biome-ignore lint/suspicious/noExplicitAny: any needed to make this fully generic
    return z.object(optionalProperties);
};
export const pinCodeStr = z.string().refine(it => it.match(/^(\d{6}|\d{8})$/), {
    message: "pincode is expected to have 6/8 digits",
});
export const parseWith = (schema, val, subject) => {
    try {
        return schema.parse(val);
    }
    catch (e) {
        throw new ChainedError(`${subject ?? "value"} does not conform to expected schema`, e);
    }
};
export const scalar = z.string().or(z.number()).or(z.boolean());
export const scalarDict = z.record(z.string(), scalar.or(scalar.array()).nullish());
export const INRValue = z
    .number()
    .or(z.string())
    .transform(val => {
    if (isString(val))
        return ensureNN(stripCurrencyPrefix(val)).trim();
    return `${val}`;
});
export const partialDateStr = z
    .string()
    .trim()
    .refine(val => {
    try {
        gracefullyParseDate(val);
        return true;
    }
    catch (e) {
        return false;
    }
});
export const ensureParseable = (zType, val) => {
    zType.parse(val);
    return val;
};
export const isParseable = (val, zType) => {
    const res = zType.safeParse(val);
    return !res.error;
};
export const ifParseable = (val, zType, handle) => {
    const res = zType.safeParse(val);
    if (res.error)
        return null;
    return handle(res.data);
};
export const parsedJSON = z.any().transform(t => {
    if (typeof t === "string") {
        return JSON.parse(t);
    }
    return t;
});
//# sourceMappingURL=zod.js.map